/**
 * Overland訪問カード
 *
 * タイムラインに表示する滞在場所カード
 * Place情報（画像、住所、カテゴリ、ジャンル）を表示
 * タップで場所名編集シートを表示
 */

import {
  Clock,
  ExternalLink,
  Footprints,
  MapPin,
  MessageSquarePlus,
  Pencil,
  Square,
  SquareCheck,
  Tag,
  Wifi,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import type { ImmichAsset } from '@/api/services/immich'
import type { CardTransaction } from '@/api/types/card-transaction'
import type { OverlandLocation, OverlandVisit, Place } from '@/api/types/overland'
import type { ReceiptSummary } from '@/api/types/receipt'
import { AuthImage } from '@/components/ui/auth-image'
import { Badge } from '@/components/ui/badge'
import { formatArrivalJST, formatDistanceKm, formatStayDuration, formatTimeJST } from '@/lib/activity-format-utils'
import { calculateDistance } from '@/lib/geo-utils'
import { getPlaceName } from '@/lib/place-utils'
import { cn, stripQueryParams } from '@/lib/utils'
import { ReceiptCreateSheet } from '@/pages/receipts/components/receipt-create-sheet'
import {
  draftFromCardTransaction,
  draftFromVisit,
  emptyReceiptDraft,
  type ReceiptDraft,
} from '@/pages/receipts/receipt-create-form'

import { PhotoGrid } from './photo-grid'
import { PlaceDetailSheet } from './place-detail-sheet'
import { PlaceEditSheet } from './place-edit-sheet'
import { TimelineNoteCard } from './timeline-note-card'
import { VisitReceiptList } from './visit-receipt-list'

type Props = {
  visit: OverlandVisit
  locations?: OverlandLocation[]
  className?: string
  onAddNote?: () => void
  /** メモが編集・削除されたときの再取得コールバック */
  onNotesUpdated?: () => void | Promise<void>
  /** Visit の期間内に撮影された Immich 写真 */
  photos?: ImmichAsset[]
  /** この滞在で買ったレシート */
  receipts?: ReceiptSummary[]
  /** この滞在中のカード利用のうち、レシートの無いもの */
  cardTransactions?: CardTransaction[]
  // 選択モード用
  isSelectMode?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
}

/**
 * Place画像のサムネイルURL取得
 */
const getPlaceImageUrl = (place: Place | null | undefined): string | null => {
  if (!place?.images?.length) return null
  // 署名パラメータを除去して AuthImage に署名を任せる
  return stripQueryParams(place.images[0].url)
}

/**
 * Visit中の移動距離を計算
 */
const calculateVisitDistance = (visit: OverlandVisit, locations: OverlandLocation[]): number | null => {
  if (!visit.arrival_date || !visit.departure_date) return null
  if (locations.length < 2) return null

  const arrivalTime = new Date(visit.arrival_date).getTime()
  const departureTime = new Date(visit.departure_date).getTime()

  // Visit期間中のロケーションをフィルタ
  const visitLocations = locations
    .filter((loc) => {
      const locTime = new Date(loc.timestamp).getTime()
      return locTime >= arrivalTime && locTime <= departureTime
    })
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

  if (visitLocations.length < 2) return null

  // 総移動距離を計算
  let totalDistance = 0
  for (let i = 1; i < visitLocations.length; i++) {
    const prev = visitLocations.at(i - 1)
    const curr = visitLocations.at(i)
    if (prev && curr) {
      totalDistance += calculateDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude)
    }
  }

  return totalDistance
}

export const OverlandVisitCard = ({
  visit,
  locations = [],
  className,
  onAddNote,
  onNotesUpdated,
  photos,
  receipts,
  cardTransactions,
  isSelectMode = false,
  isSelected = false,
  onToggleSelect,
}: Props) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  // 手で登録するレシートの初期値。null の間はシートを閉じている
  const [receiptDraft, setReceiptDraft] = useState<ReceiptDraft | null>(null)
  const duration = formatStayDuration(visit.arrival_date, visit.departure_date)
  const placeName = getPlaceName(visit, '訪問場所')

  // 終了時刻の判定
  const isOngoing = !visit.departure_date

  const hasPlace = !!visit.place
  const imageUrl = getPlaceImageUrl(visit.place)
  const place = visit.place
  // Visit中の移動距離を計算
  const visitDistance = useMemo(() => calculateVisitDistance(visit, locations), [visit, locations])

  // カードクリックハンドラ
  const handleCardClick = useCallback(() => {
    if (isSelectMode) {
      onToggleSelect?.()
    } else if (hasPlace) {
      // 場所がある場合は詳細シートを開く
      setIsDetailOpen(true)
    } else {
      // 場所未設定の場合は直接編集シート（検索画面）を開く
      setIsEditOpen(true)
    }
  }, [isSelectMode, onToggleSelect, hasPlace])

  // 編集シートを開く（詳細シートを閉じてから）
  const handleOpenEdit = useCallback(() => {
    setIsDetailOpen(false)
    setIsEditOpen(true)
  }, [])

  // レシートを手で登録するシートを開く（シートを重ねないよう詳細・編集シートは閉じる）
  const handleOpenReceiptCreate = useCallback(() => {
    setIsDetailOpen(false)
    setIsEditOpen(false)
    setReceiptDraft(draftFromVisit({ arrival_date: visit.arrival_date, placeName: visit.place ? placeName : null }))
  }, [visit.arrival_date, visit.place, placeName])

  const handleCardTransactionClick = useCallback((tx: CardTransaction) => {
    setReceiptDraft(draftFromCardTransaction(tx))
  }, [])

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        {/* 選択モード時のチェックボックス（カード外側） */}
        {isSelectMode && (
          <div
            className="shrink-0 flex items-center justify-center cursor-pointer"
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
          >
            {isSelected ? (
              <SquareCheck className="size-6 text-primary" />
            ) : (
              <Square className="size-6 text-muted-foreground" />
            )}
          </div>
        )}

        {/* カード本体 */}
        <div
          className={cn(
            'flex-1 min-w-0 rounded-lg border bg-card p-3 shadow-sm cursor-pointer hover:bg-accent/50',
            // transition-all はホバー時に合成レイヤーが作られ画像がちらつくため対象プロパティを限定
            'transition-[background-color,opacity,translate] duration-200',
            isSelectMode && (isSelected ? 'opacity-100 translate-x-2' : 'opacity-60'),
            isSelected && 'ring-2 ring-primary bg-primary/5',
          )}
          onClick={handleCardClick}
          role="button"
          tabIndex={0}
          // メモ編集など子要素の Enter が伝播してきた場合は開かない
          onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && handleCardClick()}
        >
          <div className="flex items-center gap-3">
            {/* アイコンまたは画像 */}
            {imageUrl ? (
              <AuthImage src={imageUrl} alt={placeName} adaptive />
            ) : (
              <div className="rounded-lg p-2 bg-indigo-500/10 shrink-0">
                <MapPin className="size-5 text-indigo-500" />
              </div>
            )}

            {/* 内容 */}
            <div className="flex-1 min-w-0">
              <span className="flex justify-end text-xs text-muted-foreground">
                {formatArrivalJST(visit.arrival_date, visit.departure_date)} →{' '}
                <span className={isOngoing ? 'text-green-500' : ''}>
                  {isOngoing ? '滞在中' : formatTimeJST(visit.departure_date)}
                </span>
              </span>

              <div className="flex items-start gap-1.5 min-w-0">
                {/* 名前+編集ボタン（クリックで編集シート） */}
                {!isSelectMode ? (
                  <button
                    type="button"
                    className="p-0! flex items-start gap-1 min-w-0 text-left underline decoration-muted-foreground/50 underline-offset-2 hover:decoration-primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsEditOpen(true)
                    }}
                  >
                    <h3 className="wrap-break-word">{placeName}</h3>
                    <Pencil className="size-5 text-muted-foreground shrink-0 mt-0.5" />
                  </button>
                ) : (
                  <h3 className="font-medium text-sm wrap-break-word">{placeName}</h3>
                )}
              </div>

              {/* 住所（Placeにある場合） */}
              {place?.address && <p className="text-xs text-muted-foreground mt-0.5 truncate">{place.address}</p>}

              {/* カテゴリ/ジャンルバッジ */}
              {place && (place.categories.length > 0 || place.genres.length > 0) && (
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  <Tag className="size-3 text-muted-foreground shrink-0" />
                  {place.genres.slice(0, 2).map((genre) => (
                    <Badge key={genre.code} variant="secondary" className="text-[10px] px-1.5 py-0">
                      {genre.name}
                    </Badge>
                  ))}
                  {place.genres.length === 0 &&
                    place.categories.slice(0, 2).map((cat) => (
                      <Badge key={cat.name} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {cat.display_name ?? cat.name}
                      </Badge>
                    ))}
                </div>
              )}

              {/* 滞在時間 */}
              {duration && (
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <Clock className="size-3" />
                  <span className="text-foreground font-medium">{duration}</span>
                </div>
              )}

              {/* 滞在中の移動距離 */}
              {visitDistance !== null && visitDistance > 0.01 && (
                <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                  <Footprints className="size-3" />
                  <span>滞在中の移動: {formatDistanceKm(visitDistance)}</span>
                </div>
              )}

              {/* Google Mapsリンク */}
              {place?.google_maps_url && (
                <a
                  href={place.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-xs text-blue-500 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="size-3" />
                  Google Mapsで開く
                </a>
              )}

              {/* WiFi情報（場所名と異なる場合のみ表示） */}
              {visit.wifi && visit.wifi !== placeName && !hasPlace && (
                <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                  <Wifi className="size-3" />
                  <span className="truncate">{visit.wifi}</span>
                </div>
              )}

              {/* 座標（WiFiがない＆場所が未設定の場合） */}
              {!visit.wifi && !hasPlace && (
                <p className="text-xs text-muted-foreground mt-1">
                  {visit.latitude.toFixed(4)}, {visit.longitude.toFixed(4)}
                </p>
              )}
            </div>
          </div>

          {/* メモボタン＆メモ一覧（サムネ+情報の下に表示） */}
          {onAddNote && (
            <div className="mt-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
              {visit.notes?.map((note) => (
                <TimelineNoteCard
                  key={note.id}
                  note={note}
                  onChanged={onNotesUpdated}
                  onDeleted={onNotesUpdated}
                  className="text-sm"
                />
              ))}
              {(!visit.notes || visit.notes.length === 0) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddNote()
                  }}
                >
                  <MessageSquarePlus className="size-3.5" />
                  <span>メモを追加</span>
                </button>
              )}
            </div>
          )}

          {/* この滞在で買ったレシート */}
          <VisitReceiptList
            receipts={receipts}
            cardTransactions={cardTransactions}
            onCardTransactionClick={isSelectMode ? undefined : handleCardTransactionClick}
          />

          {/* Visit 期間内の Immich 写真 */}
          {photos && photos.length > 0 && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <PhotoGrid assets={photos} />
            </div>
          )}
        </div>
      </div>

      {/* 場所詳細シート */}
      <PlaceDetailSheet
        visit={visit}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onEditClick={handleOpenEdit}
        onAddReceiptClick={handleOpenReceiptCreate}
      />

      {/* 場所編集シート */}
      <PlaceEditSheet
        visit={visit}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        onAddReceiptClick={handleOpenReceiptCreate}
      />

      <ReceiptCreateSheet
        open={receiptDraft !== null}
        onOpenChange={(open) => !open && setReceiptDraft(null)}
        initialDraft={receiptDraft ?? emptyReceiptDraft()}
        visitId={visit.id}
        contextLabel={placeName}
      />
    </>
  )
}
